package dev.wyns.ventrova.ui.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.Stable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import dev.wyns.ventrova.app.DefaultCenter
import dev.wyns.ventrova.app.DefaultZoom
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.BoundingBox
import dev.wyns.ventrova.data.model.GeoPoint
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.ui.theme.palette
import kotlin.time.Duration.Companion.milliseconds
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.convertToColor
import org.maplibre.compose.expressions.dsl.exponential
import org.maplibre.compose.expressions.dsl.feature
import org.maplibre.compose.expressions.dsl.interpolate
import org.maplibre.compose.expressions.dsl.zoom
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.layers.CircleLayer
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.spatialk.geojson.Feature
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Point
import org.maplibre.spatialk.geojson.Position

/** At most this many full markers; the other places are drawn as plain dots. */
private const val MAX_MARKERS = 150

/** Below this zoom (a whole region or the country) every place is a dot. */
private const val MIN_MARKER_ZOOM = 11.0

/** Size of the marker's ring, see [PlaceMarker]. */
private const val MARKER_SIZE_DP = 44f

/** How close to a dot a tap or the mouse has to be to hit it. */
private const val DOT_TAP_RADIUS_DP = 16f
private const val DOT_HOVER_RADIUS_DP = 8f

/** Per-platform look of the map decorations. */
data class MapDecorations(
    val markerStyle: MarkerStyle,
    val locationColor: Color,
    /** Dashed radius ring (iOS, web) or a solid accent ring (Android). */
    val radiusRingColor: Color,
    val radiusRingFill: Color,
    val radiusRingDashed: Boolean,
)

/** Camera controls for the places map, shared by all platform layouts. */
@Stable
class PlacesMapController internal constructor(
    val mapState: MapState,
    internal val dots: MutableState<List<PlaceItem>>,
) {
    val zoom: Double get() = mapState.cameraPosition.zoom

    suspend fun animateTo(point: GeoPoint, zoom: Double? = null) {
        mapState.animateCamera(
            CameraUpdate(target = point.toPosition(), zoom = zoom ?: mapState.cameraPosition.zoom),
            animation = CameraAnimation.Ease(duration = 450.milliseconds),
        )
    }

    suspend fun zoomBy(delta: Double) {
        mapState.animateCamera(
            CameraUpdate(zoom = mapState.cameraPosition.zoom + delta),
            animation = CameraAnimation.Ease(duration = 300.milliseconds),
        )
    }

    /** Pans so [point] sits at ([fractionX], [fractionY]) of the visible map, like the design's focus(). */
    suspend fun focus(point: GeoPoint, fractionX: Float = 0.5f, fractionY: Float = 0.4f) {
        val size = mapState.viewport?.size ?: return animateTo(point)
        val current = mapState.screenLocationFromPosition(point.toPosition()) ?: return animateTo(point)
        val desired = DpOffset(size.width * fractionX, size.height * fractionY)
        val center = DpOffset(size.width / 2, size.height / 2)
        val newCenter = mapState.positionFromScreenLocation(
            DpOffset(center.x + (current.x - desired.x), center.y + (current.y - desired.y)),
        ) ?: return animateTo(point)
        mapState.animateCamera(CameraUpdate(target = newCenter), animation = CameraAnimation.Ease(duration = 450.milliseconds))
    }
}

@Composable
fun rememberPlacesMapController(initialCenter: GeoPoint = DefaultCenter, initialZoom: Double = DefaultZoom): PlacesMapController {
    val dots = remember { mutableStateOf(emptyList<PlaceItem>()) }
    val mapState = rememberMapState(
        baseStyle = BaseStyle.Uri(Res.getUri("files/map_style.json")),
        initialCameraPosition = CameraPosition(target = initialCenter.toPosition(), zoom = initialZoom),
    ) {
        PlaceDots(dots.value)
    }
    return remember(mapState) { PlacesMapController(mapState, dots) }
}

/**
 * The map with the design's markers, the user's location and the distance ring. Reports the visible
 * area (with some margin) whenever the camera comes to rest.
 */
@Composable
fun PlacesMap(
    controller: PlacesMapController,
    places: List<PlaceItem>,
    selectedId: String?,
    decorations: MapDecorations,
    onMarkerClick: (PlaceItem) -> Unit,
    onMapClick: () -> Unit,
    onViewportChanged: (BoundingBox, GeoPoint) -> Unit,
    modifier: Modifier = Modifier,
    userLocation: GeoPoint? = null,
    radiusCenter: GeoPoint? = null,
    radiusKm: Double? = null,
    /** Marker whose name is shown, e.g. while hovered on web. */
    highlightedId: String? = null,
    viewportInsets: PaddingValues = PaddingValues(0.dp),
    onMarkerHover: ((String?) -> Unit)? = null,
) {
    val mapState = controller.mapState

    // Places that get a full marker without overlapping another one, by priority. Recomputed when
    // the camera comes to rest, so markers don't flicker while panning or zooming.
    var layout by remember { mutableStateOf(MarkerLayout.Empty) }
    val labelHeight = decorations.markerStyle.labelHeight.value + 3f
    LaunchedEffect(mapState, places, selectedId, labelHeight) {
        snapshotFlow { if (mapState.isCameraMoving) null else mapState.viewport }
            .filterNotNull()
            .collect { viewport ->
                val width = viewport.size.width.value
                val height = viewport.size.height.value
                val boxes = places.sortedWith(markerPriority(selectedId)).mapNotNull { item ->
                    val point = mapState.screenLocationFromPosition(item.place.location.toPosition()) ?: return@mapNotNull null
                    val x = point.x.value
                    val y = point.y.value
                    // Off-screen places stay dots, so they don't take the place of visible ones.
                    if (x < -MARKER_SIZE_DP || y < -MARKER_SIZE_DP || x > width + MARKER_SIZE_DP || y > height + MARKER_SIZE_DP) {
                        return@mapNotNull null
                    }
                    val selected = item.id == selectedId
                    val hasLabel = selected || item.openStatus?.isOpen == false
                    MarkerBox(
                        id = item.id,
                        centerX = x,
                        centerY = y,
                        width = if (selected) estimatedNameLabelWidth(item.place.name) else MARKER_SIZE_DP,
                        height = MARKER_SIZE_DP + if (hasLabel) labelHeight else 0f,
                    )
                }
                val maxMarkers = if (viewport.cameraPosition.zoom < MIN_MARKER_ZOOM) 0 else MAX_MARKERS
                layout = declutterMarkers(boxes, maxMarkers)
            }
    }
    // The selected and hovered places always get a full marker; hovering doesn't push others away.
    fun isMarker(id: String) = id in layout.markers || id == selectedId || id == highlightedId
    val markers = places.filter { isMarker(it.id) }
    controller.dots.value = places.filterNot { isMarker(it.id) }
    val currentLayout by rememberUpdatedState(layout)
    val currentPlaces by rememberUpdatedState(places)
    val currentOnMarkerClick by rememberUpdatedState(onMarkerClick)

    val currentOnViewportChanged by rememberUpdatedState(onViewportChanged)
    LaunchedEffect(mapState) {
        snapshotFlow { if (mapState.isCameraMoving) null else mapState.viewport }
            .filterNotNull()
            .collect { viewport ->
                val bounds = viewport.visibleBounds
                val padLat = (bounds.north - bounds.south) * 0.25
                val padLng = (bounds.east - bounds.west) * 0.25
                currentOnViewportChanged(
                    BoundingBox(bounds.south - padLat, bounds.west - padLng, bounds.north + padLat, bounds.east + padLng),
                    GeoPoint(viewport.cameraPosition.target.latitude, viewport.cameraPosition.target.longitude),
                )
            }
    }

    val currentOnMapClick by rememberUpdatedState(onMapClick)
    val interactions = remember {
        MapInteractions {
            camera { tilt { enabled = false } }
            callbacks {
                click {
                    onEvent { event ->
                        // Dots are drawn by the map, so a tap on one arrives as a map click.
                        val dot = currentLayout.dotAt(event.screenOffset.x.value, event.screenOffset.y.value, DOT_TAP_RADIUS_DP)
                        val item = dot?.let { box -> currentPlaces.firstOrNull { it.id == box.id } }
                        if (item != null) {
                            currentOnMarkerClick(item)
                            ClickResult.Consume
                        } else {
                            currentOnMapClick()
                            ClickResult.Pass
                        }
                    }
                }
            }
        }
    }

    MaplibreMap(
        modifier = modifier.revealHiddenDots(
            onHover = onMarkerHover,
            isActive = { !mapState.isCameraMoving },
            layout = { currentLayout },
            // A revealed marker shows its name, so it covers more than its dot's box.
            revealedBox = { box ->
                val name = currentPlaces.firstOrNull { it.id == box.id }?.place?.name.orEmpty()
                box.copy(width = estimatedNameLabelWidth(name), height = MARKER_SIZE_DP + labelHeight)
            },
        ),
        state = mapState,
        viewportInsets = viewportInsets,
        interactions = interactions,
    ) {
        val metersPerDp = mapState.viewport?.metersPerDpAtTarget
        if (radiusKm != null && radiusCenter != null && metersPerDp != null && metersPerDp > 0) {
            val diameter = (radiusKm * 2000 / metersPerDp).dp
            Canvas(Modifier.size(diameter).placedAt(radiusCenter.toPosition())) {
                drawCircle(decorations.radiusRingFill)
                drawCircle(
                    color = decorations.radiusRingColor,
                    style = Stroke(
                        width = (if (decorations.radiusRingDashed) 1.5.dp else 2.dp).toPx(),
                        pathEffect = if (decorations.radiusRingDashed) PathEffect.dashPathEffect(floatArrayOf(6f, 6f)) else null,
                    ),
                )
            }
        }

        if (userLocation != null) {
            LocationPuck(decorations.locationColor, Modifier.placedAt(userLocation.toPosition()))
        }

        markers.forEach { item ->
            val selected = item.id == selectedId
            val highlighted = item.id == highlightedId
            key(item.id) {
                PlaceMarker(
                    item = item,
                    selected = selected,
                    showName = selected || highlighted,
                    style = decorations.markerStyle,
                    onClick = { onMarkerClick(item) },
                    modifier = Modifier
                        .placedAt(item.place.location.toPosition())
                        .zIndex(if (selected || highlighted) 1f else 0f)
                        .hover(item.id, onMarkerHover),
                )
            }
        }
    }
}

@Composable
private fun LocationPuck(color: Color, modifier: Modifier) {
    Box(modifier.size(46.dp)) {
        Box(Modifier.fillMaxSize().background(color.copy(alpha = 0.16f), CircleShape))
        Box(
            Modifier
                .size(18.dp)
                .align(Alignment.Center)
                .shadow(3.dp, CircleShape)
                .background(color, CircleShape)
                .border(3.dp, Color.White, CircleShape),
        )
    }
}

/** Plain level-colored dots for zoomed-out views with many places. */
@Composable
private fun PlaceDots(places: List<PlaceItem>) {
    val features = remember(places) {
        FeatureCollection(
            places.map { item ->
                Feature(
                    geometry = Point(item.place.location.toPosition()),
                    properties = buildJsonObject {
                        put("id", item.id)
                        put("color", item.place.veganLevel.palette.color.toHex())
                    },
                )
            },
        )
    }
    val source = rememberGeoJsonSource(GeoJsonData.Features(features))
    CircleLayer(
        id = "place-dots",
        source = source,
        color = feature["color"].convertToColor(),
        radius = interpolate(exponential(1.5f), zoom(), 7 to const(2.5.dp), 13 to const(5.dp)),
        strokeColor = const(Color.White),
        strokeWidth = const(1.dp),
    )
}

/**
 * Mouse hover over a dot whose marker a neighbor pushed away reports that place to [onHover], so
 * it shows as a full marker; it stays while the mouse is over where that marker is drawn. Dots in
 * the zoomed-out overview don't count. A no-op without [onHover].
 */
private fun Modifier.revealHiddenDots(
    onHover: ((String?) -> Unit)?,
    isActive: () -> Boolean,
    layout: () -> MarkerLayout,
    revealedBox: (MarkerBox) -> MarkerBox,
): Modifier =
    if (onHover == null) {
        this
    } else {
        pointerInput(Unit) {
            var revealed: MarkerBox? = null
            awaitPointerEventScope {
                while (true) {
                    val event = awaitPointerEvent()
                    val change = event.changes.firstOrNull() ?: continue
                    val x = change.position.x.toDp().value
                    val y = change.position.y.toDp().value
                    val current = layout()
                    val next = when {
                        event.type == PointerEventType.Exit || change.pressed || !isActive() -> null
                        revealed?.let { it.contains(x, y) && it.id in current.hiddenByNeighbors } == true -> revealed
                        else -> current.dotAt(x, y, DOT_HOVER_RADIUS_DP, onlyHidden = true)?.let(revealedBox)
                    }
                    if (next?.id != revealed?.id) {
                        // A full marker under the mouse reports its own hover.
                        if (next != null || current.markerAt(x, y) == null) onHover(next?.id)
                        revealed = next
                    }
                }
            }
        }
    }

/** Reports pointer enter/exit (mouse hover on web); a no-op without [onHover]. */
private fun Modifier.hover(id: String, onHover: ((String?) -> Unit)?): Modifier =
    if (onHover == null) {
        this
    } else {
        pointerInput(id) {
            awaitPointerEventScope {
                while (true) {
                    when (awaitPointerEvent().type) {
                        PointerEventType.Enter -> onHover(id)
                        PointerEventType.Exit -> onHover(null)
                    }
                }
            }
        }
    }

/** Rough width of a marker whose label shows the name (capped like the label in [PlaceMarker]). */
private fun estimatedNameLabelWidth(name: String): Float = (name.length * 6.5f + 14f).coerceIn(MARKER_SIZE_DP, 174f)

internal fun GeoPoint.toPosition() = Position(longitude = longitude, latitude = latitude)

private fun Color.toHex(): String {
    val argb = (alpha * 255).toInt() shl 24 or ((red * 255).toInt() shl 16) or ((green * 255).toInt() shl 8) or (blue * 255).toInt()
    return "#" + (argb and 0xFFFFFF).toString(16).padStart(6, '0')
}
